import React, { useEffect } from 'react';
import { kpiMeta } from '../config/kpi-config';
import LazyAnalysisTabs from './LazyAnalysisTabs';
import { useFilters } from '../context/FiltersContext';

interface FullScreenModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activeKpi: string;
  data: {
    latestYear: number;
    trendData: any[];
    dynamicTrendData: any[];
    distributionData: any[];
    dynamicDistributionData: any[];
    driverData: any;
    insightData: any;
    correlationData: {[driver: string]: any[]};
    riketData: {[driver: string]: {year: number, r: number}[]};
    loading: boolean;
  };
  contextInfo?: {
    municipalityId?: string;
    municipalityName?: string;
    year?: number;
    skolaId?: string | null;
  };
}

const FullScreenModal: React.FC<FullScreenModalProps> = ({
  open,
  onOpenChange,
  activeKpi,
  data,
  contextInfo
}) => {
  const name = kpiMeta[activeKpi]?.name || "Valt nyckeltal";
  const { value: globalFilters } = useFilters();
  const governanceLabel = (() => {
    const g = globalFilters.huvudmanId;
    if (g === 'kommunal') return 'Kommunal';
    if (g === 'fristående') return 'Fristående';
    return 'Alla';
  })();

  // Prevent body scroll when modal is open and handle Leaflet z-index clashes
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      // Force background maps to be inert to prevent z-index bleed and interaction issues
      const backgroundMaps = document.querySelectorAll('.leaflet-container:not(.fullscreen-modal .leaflet-container)');
      backgroundMaps.forEach(m => (m as HTMLElement).style.visibility = 'hidden');
    } else {
      document.body.style.overflow = 'auto';
      const backgroundMaps = document.querySelectorAll('.leaflet-container');
      backgroundMaps.forEach(m => (m as HTMLElement).style.visibility = 'visible');
    }
    
    return () => {
      document.body.style.overflow = 'auto';
      const backgroundMaps = document.querySelectorAll('.leaflet-container');
      backgroundMaps.forEach(m => (m as HTMLElement).style.visibility = 'visible');
    };
  }, [open]);

  return (
    <div className={`fixed inset-0 z-[9999] ${open ? 'block' : 'hidden'} fullscreen-modal`}>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black/60 backdrop-blur-md transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0'}`}
        onClick={() => onOpenChange(false)}
      />
      
      {/* Full-screen modal content */}
      <div 
        className={`fixed inset-0 bg-white dark:bg-slate-900 overflow-hidden transform transition-transform duration-300 ease-out flex flex-col ${open ? 'translate-y-0' : 'translate-y-full'}`}
      >
        {/* Header with context */}
        <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-4 py-3 z-10 shrink-0">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 truncate">
                {name}
              </h2>
              {contextInfo && (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {contextInfo.municipalityName} • {contextInfo.year} • {governanceLabel}
                </p>
              )}
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="ml-4 p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-400 min-h-[44px] min-w-[44px]"
              aria-label="Stäng analys"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-6">
          <LazyAnalysisTabs
            activeKpi={activeKpi}
            kpiName={name}
            latestYear={data.latestYear}
            trendData={data.trendData}
            dynamicTrendData={data.dynamicTrendData}
            distributionData={data.distributionData}
            dynamicDistributionData={data.dynamicDistributionData}
            driverData={data.driverData}
            insightData={data.insightData}
            correlationData={data.correlationData}
            riketData={data.riketData}
            kommunId={contextInfo?.municipalityId}
            skolaId={contextInfo?.skolaId}
            loading={data.loading}
          />
        </div>
        
        {/* ARIA live region for dynamic content announcements */}
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          {data.loading ? "Laddar data..." : `Analys för ${name} är redo`}
        </div>
      </div>
    </div>
  );
};

export default FullScreenModal;
